import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Badge, Card, Checkbox, IconButton, LinearProgress, Link,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, InputAdornment, CircularProgress, Grid, FormControl, Autocomplete
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HorizontalSplitIcon from '@mui/icons-material/HorizontalSplit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CloseIcon from '@mui/icons-material/Close'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import StarRateIcon from '@mui/icons-material/StarRate';
import SaveIcon from '@mui/icons-material/Save'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import { ClassNameMap, withStyles } from '@mui/styles';
import { toast } from 'react-toastify';
import moment from 'moment';
import { TaskService } from './tasks.service';
import Helper from '../../utils/helper';
import stylesheet from '../patients/detail/detail-container.stylesheet';
import TaskActionComponent from '../patients/detail/info-header/task-action.component';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { taskHeaderCol } from './header-config';
import TaskDelayComponent from '../patients/detail/info-header/task-delay.component';
import ConfirmationDialogComponent from '../patients/detail/common/confirmation-dialog.component';
import TaskBpComponent from '../patients/detail/info-header/task-bp.component';
import { useLocation } from 'react-router-dom';
import TaskHistoryComponent from './task-history.component';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded'
import { getZoomPhoneUri } from '../../utils/phone-utils'
import { stripPrefixAnywhere } from '../../utils/display-name-formatter'
import { taskHospitalPrefilterFor } from '../patients/detail/comp-config'

interface IProps {
  assigneeId: string | null,
  handleTaskLoading?: any
  classes: ClassNameMap
}

const AllTaskListComponent: React.FC<IProps> = ({ assigneeId, handleTaskLoading, classes }) => {
  const location: any = useLocation();
  const taskPage = location.pathname == '/all-tasks' ? 'All' : location.pathname == '/top-tasks' ? 'Top' : 'My'
  const [taskLoading, setTaskLoading] = useState(false) as any
  const [currTask, setCurrTask] = useState({}) as any
  const [confirmDelete, setConfirmDelete] = useState(null) as any
  const [currTaskNote, setCurrTaskNote] = useState({}) as any
  const taskService = new TaskService();
  const tableEl = useRef<any>()
  const [taskData, setTaskData] = useState([]) as any
  const [isInitialLoad, setIsInitialLoad] = useState(false)
  const [taskFilter, setTaskFilter] = useState({}) as any;
  const [excludeOptionalChecked, setExcludeOptionalChecked] = useState(false) as any;
  const [usersList, setUsersList] = useState([]) as any
  const [searchInput, setSearchInput] = useState("") as any;
  const [limit] = useState(50) as any
  const [taskTotal, setTaskTotal] = useState({ total: '', showing: '' }) as any
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)
  const programsList = useSelector((state: RootState) => state.metaData.metadata.PROGRAMS || [])
  const hospitalsMetadata = useSelector((state: RootState) => (state as any).metaData?.metadata?.HOSPITALS || {})
  const headerCol = taskHeaderCol
  const sortableColumns = ["assignment_date", "task_status", "due_date", "assigned_to", "title"];

  const taskTypeObj = {
    'To Do': 'To Do',
    'Attempted': 'Attempted',
    'delayed': 'Delayed',
    'Intentionally Delayed': 'Delayed',
    'Upcoming': 'Scheduled',
    'Completed': 'Completed',
    'Canceled': 'Cancelled',
  } as any

  const formatContactTime = (timeBlocks: string, timezone?: string) => {
    const compact = timeBlocks.split(',').map(b => b.replace(/\s*\(.*?\)/, '').trim()).join(', ')
    const tz = timezone ? timezone.slice(0, 1) + 'T' : 'ET'
    return `${compact} ${tz}`
  }

  useEffect(() => {
    setIsInitialLoad(false)

    const defaultFilter =
      taskPage === 'Top'
        ? {
            isDueToday: true,
            isAssignedToday: false,
            isCompleted: true,
            isHideUpdatedToday: false,
            isTopDailyTask: false,
            inclusionFilter: '',
            priority: '',
            includeAdHocToday: true,
            hospitalFilter: taskHospitalPrefilterFor('lifemd,boylston,healthyheart,chambercardio'),
            search: '',
            page: 1,
            sortBy: '',
            sortOrder: '',
          }
        : {
            isDueToday: false,
            isAssignedToday: false,
            isCompleted: true,
            isHideUpdatedToday: false,
            isTopDailyTask: false,
            inclusionFilter: '',
            priority: '',
            includeAdHocToday: false,
            hospitalFilter: taskHospitalPrefilterFor(taskPage === 'All' ? 'lifemd,boylston,healthyheart,chambercardio' : 'lifemd,boylston,chambercardio'),
            search: '',
            page: 1,
            sortBy: '',
            sortOrder: '',
          }

    setTaskFilter(defaultFilter)
    setIsInitialLoad(true)
  }, [location.pathname])

  useEffect(() => {
    handleTaskLoading(taskLoading)
  }, [taskLoading])

  useEffect(() => {
    if (Object.keys(taskFilter)?.length > 0 && assigneeId && isInitialLoad && !taskLoading) {
      getTaskList()
    }
  }, [taskFilter, isInitialLoad, assigneeId]);

  const getTaskList = useCallback(() => {
    if (taskLoading || (taskPage === 'My' && !assigneeId) || (taskPage === 'Top' && !assigneeId))
      return;

    let targetAssigneeId = ((taskPage === 'My') || (taskPage === 'Top')) ? assigneeId : '*';
    setTaskLoading(true);
    let subsampled = (taskPage === 'Top' || taskFilter?.isTopDailyTask) ? true : false;
    let isDueToday = (taskPage === 'Top' || taskFilter?.isDueToday) ? true : false;

    taskService.getPatientTasks(
      targetAssigneeId,
      '',
      '',
      taskFilter?.inclusionFilter || '',
      taskFilter?.priority || '',
      null,
      null,
      excludeOptionalChecked,
      isDueToday,
      taskFilter?.isCompleted || false,
      taskFilter?.isHideUpdatedToday || false,
      taskFilter?.search || '',
      taskFilter?.sortBy || '',
      taskFilter?.sortOrder || '',
      limit,
      taskFilter?.page || 1,
      subsampled,
      taskFilter?.includeAdHocToday,
      true,
      taskFilter?.hospitalFilter || null,
      taskFilter?.isAssignedToday || false
    ).then((res: any) => {
      let taskList: any[] = res?.data?.tasks || [];
      setTaskLoading(false);
      setUsersList(res?.data?.picklist_values?.users || [])

      if (taskFilter?.page > 1) {
        setTaskData([...taskData, ...taskList])
        setTaskTotal({
          total: res?.data?.total,
          showing: (taskData.length + (res?.data?.tasks?.length || 0))
        })
      } else {
        tableEl?.current?.scrollTo(0, 0)
        setTaskData(taskList)
        setTaskTotal({ total: res?.data?.total, showing: res?.data?.tasks?.length || 0 })
      }
    }).catch((err: any) => {
      setTaskLoading(false)
      toast.error(Helper.getError(err), Helper.bottom_center())
    })
  }, [taskFilter, isInitialLoad]);

  const fetchNextSetData = () => {
    if (!taskLoading && (taskTotal.showing !== taskTotal.total)) {
      setTaskFilter({ ...taskFilter, page: (taskFilter.page + 1) })
    }
  }

  const scrollListener = useCallback(() => {
    const tableElement = tableEl?.current;
    if (!tableElement) return;

    const scrollTop = tableElement.scrollTop;
    const scrollHeight = tableElement.scrollHeight;
    const clientHeight = tableElement.clientHeight;

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const triggerDistance = Math.round(clientHeight * 0.2);

    if (
      !taskLoading &&
      distanceFromBottom < triggerDistance &&
      taskTotal.showing < taskTotal.total
    ) {
      fetchNextSetData();
    }
  }, [taskLoading, taskTotal, fetchNextSetData]);

  useEffect(() => {
    const tableElement = tableEl?.current;
    if (!tableElement) return;

    let throttleTimer = '' as any
    const throttledScrollListener = () => {
      if (throttleTimer) return;

      throttleTimer = setTimeout(() => {
        scrollListener();
        throttleTimer = null;
      }, 150);
    };

    tableElement.addEventListener('scroll', throttledScrollListener);
    window.addEventListener('resize', throttledScrollListener);

    return () => {
      tableElement.removeEventListener('scroll', throttledScrollListener);
      window.removeEventListener('resize', throttledScrollListener);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [scrollListener])

  const getPrgName = (prg: any) => programsList?.find((f: { name: any; }) => f.name === prg)?.display_name || prg

  const handleFilterChange = (e: any) => {
    const updatedFilter = {
      ...taskFilter,
      page: 1,
      [e.target.name]: e.target.name === 'search' ? (e.target.value).trim() : e.target.checked
    }
    setTaskFilter(updatedFilter)
    localStorage.setItem(taskPage === 'My' ? 'my_task' : taskPage === 'Top' ? 'top_task' : 'all_task', JSON.stringify(updatedFilter))
  }

  useEffect(() => {
    setSearchInput(taskFilter?.search || '');
  }, [taskFilter?.search])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (isInitialLoad && !taskLoading) {
        handleFilterChange({ target: { name: 'search', value: searchInput } });
      }
    }, 1000);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const handleSort = (colName: string) => {
    if (colName) {
      const sortOrder = taskFilter?.sortOrder === 'desc'
        ? ''
        : taskFilter?.sortOrder === "asc" ? "desc" : "asc"

      const updatedFilter = {
        ...taskFilter,
        page: 1,
        sortOrder: sortOrder,
        sortBy: sortOrder !== '' ? colName : ''
      }
      setTaskFilter(updatedFilter)
      localStorage.setItem(taskPage === 'My' ? 'my_task' : taskPage === 'Top' ? 'top_task' : 'all_task', JSON.stringify(updatedFilter))
    }
  };

  const handleCloseTaskAction = () => setCurrTask({})

  const handleConfirmDelete = () => setConfirmDelete(null)

  const confirmDeleteTask = () => {
    taskService.deleteTask(confirmDelete).then((res: any) => {
      if (res?.data?.message === "Task deleted successfully") {
        getTaskList()
        setConfirmDelete(null)
      }
    }).catch((error: any) => {
      setConfirmDelete(null)
      toast.error(Helper.getError(error), Helper.bottom_center())
    })
  }

  const scrollToLeft = () => setTimeout(() => { tableEl.current.scrollTo({ left: 0, behavior: 'smooth' }) }, 10)

  const handleEditTask = (task: any, action: string) => {
    if (tableEl.current)
      scrollToLeft()

    if (currTaskNote)
      setCurrTaskNote({})

    setCurrTask({
      id: task.id,
      owner_uuid: task?.owner?.sso_id || '',
      tracking_type: task.tracking_type,
      title: task.title,
      task_status: task?.task_status,
      action_type: task?.action_type,
      due_date: task?.due_date || null,
      note: task?.notes[0]?.text || '',
      owner_role: task.owner_role,
      action: action,
      optional: task.optional,
      patient_id: task?.patient?.ref_id,
      is_adhoc: task?.is_adhoc,
      attempts: task.attempts,
      delays: task.delays,
      ref_id: task?.ref_id || null,
      ref_type: task?.ref_type || null,
      test: task?.test || null,
      assignment: {
        assigned_to: task?.assignee?.name || '',
        assignment_date: task?.assignment_date
      }
    });
  }

  const handleNoteOpen = (id: any, note: any) => setCurrTaskNote({ id: id, note: note })

  const handleSaveNote = () => {
    taskService.putTaskSchedule(currTaskNote.id, {
      'note': currTaskNote.note,
      is_updated_today: true
    }).then((res: any) => {
      setTaskData(
        taskData.map((i: any) => {
          if (i.id === currTaskNote.id) {
            return {
              ...res?.data?.task,
              patient: i.patient,
            }
          } else {
            return { ...i }
          }
        })
      )
      setCurrTaskNote({})
    }).catch((err: any) => {
      setCurrTaskNote({})
      toast.error(Helper.getError(err), Helper.bottom_center())
    }).finally(() => (currTask) && handleCloseTaskAction())
  }

  const updateNote = (e: any) => setCurrTaskNote({ ...currTaskNote, note: e.target.value })

  const getTaskStatus = (task_status: any) => {
    let status = taskTypeObj[task_status]

    return <Grid item sx={{ minWidth: '10%', width: 'fit-content !important' }}>
      <Typography variant="body2" pr={1} className={classes.desc} align="right">
        {status.length > 9 ? <Tooltip title={status}>
          <Typography component={'span'} className={classes.desc} variant="body2">
            {Helper.toCamelCase(status.length > 9 ? status.slice(0, 8) + "..." : status)}
          </Typography>
        </Tooltip>
          : <Typography component={'span'} className={`${classes.desc}`} variant="body2">
            {Helper.toCamelCase(status)}
          </Typography>}
      </Typography>
    </Grid>
  }

  const updateTaskData = (currTaskObj: any) => {
    setTaskData(
      taskData.map((i: any) => {
        if (i.id === currTask.id) {
          return {
            ...currTaskObj,
            patient: i.patient,
          }
        } else {
          return { ...i }
        }
      }))
  }

  const renderDelayIcon = (data: any) => {
    let title = 'Delay comment' as any
    let cProps = {} as any
    if (data?.count > 0) {
      title = <Card className={classes.taskDetailsTooltip}>
        <TaskDelayComponent input={{ delays: data }} />
      </Card>
      cProps = {
        tooltip: {
          sx: {
            backgroundColor: 'var(--color-background-subtle) !important',
            maxWidth: '35rem !important',
            padding: '0px !important'
          }
        }
      }
    }
    return <Tooltip title={title} placement="bottom" componentsProps={cProps}>
      <IconButton aria-label='Delay comment' size='small'
        sx={{ padding: '5px 0px 0px 0px', '& .MuiBadge-badge': { right: '-2px !important', } }}
      >
        <HourglassBottomIcon className={classes.patientTaskIcon} sx={{ fontSize: '0.65rem !important', }} />
      </IconButton>
    </Tooltip>
  }

  const renderTopDailyIcon = (top_flag: boolean) => {
    if (top_flag == true)
      return <Checkbox
        checked
        disabled
        icon={<CheckBoxOutlineBlankIcon />}
        checkedIcon={<CheckBoxIcon style={{ color: '#4CAF50' }} />}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          marginLeft: '0px !important',
          '& .MuiSvgIcon-root': {
            fontSize: '0.8rem !important',
          },
        }}
      />
    return <></>
  }

  const renderPriorityIcon = (priority_category: any) => {
    if (priority_category == 'A')
      return <KeyboardDoubleArrowUpIcon className={classes.patientTaskIcon}
        sx={{
          fontSize: '1.0rem !important',
          marginLeft: '0px !important',
          fill: '#C53030'
        }} />
    if (priority_category == 'B')
      return <KeyboardArrowUpIcon className={classes.patientTaskIcon}
        sx={{
          fontSize: '1.0rem !important',
          marginLeft: '0px !important',
          fill: '#DD6B20'
        }} />
    if (priority_category == 'C')
      return <HorizontalSplitIcon className={classes.patientTaskIcon}
        sx={{
          fontSize: '1.0rem !important',
          marginLeft: '0px !important',
          fill: '#D69E2E'
        }} />
    if (priority_category == 'D')
      return <KeyboardArrowDownIcon className={classes.patientTaskIcon}
        sx={{
          fontSize: '1.0rem !important',
          marginLeft: '0px !important',
          fill: '#38A169'
        }} />
    if (priority_category == 'E')
      return <KeyboardDoubleArrowDownIcon className={classes.patientTaskIcon}
        sx={{
          fontSize: '1.0rem !important',
          marginLeft: '0px !important',
          fill: '#3182CE'
        }} />
    return <></>
  }

  const renderAttemptIcon = () => {
    return <IconButton
      aria-label='Attempts details'
      size='small'
      sx={{ padding: '5px 0px 0px 0px' }}
    >
      <CallIcon className={classes.patientTaskIcon}
        sx={{
          fontSize: '0.65rem !important',
          marginLeft: '0px !important',
          transform: 'rotate(16deg) !important',
          fill: '#4ccd4c'
        }} />
    </IconButton>
  }

  const sortIcon = (sort: any) => {
    return (sort === 'desc')
      ? <ArrowDropDownIcon className={classes.fontSizeSmall} />
      : (sort === 'asc')
        ? <ArrowDropUpIcon className={classes.fontSizeSmall} />
        : <UnfoldMoreIcon className={classes.fontSizeSmall} />
  }

  const getGeneralNote = (note: any) => {
    const currNote = note.length > 15 ? (note).slice(0, 14) + "..." : note
    return <Tooltip title={note}>
      {currNote}
    </Tooltip>
  }

  const getTableStyle = () => {
    const obj = {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      width: '100%',
      maxHeight: `${window.innerHeight - 140}px !important`,
    }
    return { ...obj }
  }

  const renderTypeFilter = () => {
    let typeOptions = [] as any
    if (taskPage === 'Top') {
      typeOptions = [
        { key: 'di', label: 'Data intake' },
        { key: 'etc', label: 'Early tox check' },
        { key: 'mtc', label: 'Maintenance call' },
        { key: 'me', label: 'Med evaluation' },
        { key: 'mi', label: 'Med initiation' },
        { key: 'nag', label: 'Not at goal call' },
        { key: 'ebp', label: 'Extreme BP call' },
        { key: 'obc', label: 'Onboarding call' },
        { key: 'oth', label: 'Others' },
        { key: 'pvpc', label: 'Pre-visit Prep: labs, CCD' },
        { key: 'ag', label: 'Was at goal call' },
        { key: 'wc', label: 'Welcome call' },
        { key: 'rcm', label: 'RCM Tasks' },
        { key: 'ccdd', label: 'CCD download' },
        { key: 'cpab', label: 'Call patient after booking' },
        { key: 'cb', label: 'Capture biometrics' },
        { key: 'ca', label: 'Confirm address' },
        { key: 'ccr', label: 'Confirm consent received' },
        { key: 'ccp', label: 'Confirm contact preferences' },
        { key: 'cdob', label: 'Confirm date of birth' },
        { key: 'ce', label: 'Confirm email' },
        { key: 'elne', label: 'Elation lab note entry for lab check' },
        { key: 'evi', label: 'Enter and validate insurance' },
        { key: 'ld', label: 'Lab download' },
        { key: 'race', label: 'Run AI CCD Extraction' },
        { key: 'ralpe', label: 'Run AI Lab PDF Extraction' },
        { key: 'ssts', label: 'Screen and select testing site' },
        { key: 'sto', label: 'Submit test order' },
        { key: 'npot', label: 'Notify patient of ordered test' },
        { key: 'csd', label: 'Capture scheduled date' },
        { key: 'outr', label: 'Obtain and upload test report' },
        { key: 'rutr', label: 'Review and update test results' },
        { key: 'ipsf', label: 'Inform patient to schedule follow-up' },
      ]
    } else {
      typeOptions = [
        { key: 'ad', label: 'Adhoc' },
        { key: 'di', label: 'Data intake' },
        { key: 'etc', label: 'Early tox check' },
        { key: 'mtc', label: 'Maintenance call' },
        { key: 'me', label: 'Med evaluation' },
        { key: 'mi', label: 'Med initiation' },
        { key: 'nag', label: 'Not at goal call' },
        { key: 'ebp', label: 'Extreme BP call' },
        { key: 'obc', label: 'Onboarding call' },
        { key: 'oth', label: 'Others' },
        { key: 'pvpc', label: 'Pre-visit Prep: labs, CCD' },
        { key: 'ag', label: 'Was at goal call' },
        { key: 'wc', label: 'Welcome call' },
        { key: 'rcm', label: 'RCM Tasks' },
        { key: 'ccdd', label: 'CCD download' },
        { key: 'cpab', label: 'Call patient after booking' },
        { key: 'cb', label: 'Capture biometrics' },
        { key: 'ca', label: 'Confirm address' },
        { key: 'ccr', label: 'Confirm consent received' },
        { key: 'ccp', label: 'Confirm contact preferences' },
        { key: 'cdob', label: 'Confirm date of birth' },
        { key: 'ce', label: 'Confirm email' },
        { key: 'elne', label: 'Elation lab note entry for lab check' },
        { key: 'evi', label: 'Enter and validate insurance' },
        { key: 'ld', label: 'Lab download' },
        { key: 'race', label: 'Run AI CCD Extraction' },
        { key: 'ralpe', label: 'Run AI Lab PDF Extraction' },
        { key: 'ssts', label: 'Screen and select testing site' },
        { key: 'sto', label: 'Submit test order' },
        { key: 'npot', label: 'Notify patient of ordered test' },
        { key: 'csd', label: 'Capture scheduled date' },
        { key: 'outr', label: 'Obtain and upload test report' },
        { key: 'rutr', label: 'Review and update test results' },
        { key: 'ipsf', label: 'Inform patient to schedule follow-up' },
      ]
    }

    typeOptions.sort((a: any, b: any) => a.label.localeCompare(b.label))
    const getSelectedValues = () => {
      if (!taskFilter?.inclusionFilter) return []
      const filterKeys = taskFilter.inclusionFilter.split(',')
      return typeOptions.filter((option: { key: string }) => filterKeys.includes(option.key))
    }

    let typeData = getSelectedValues()
    return (
      <Stack direction={'column'}>
        <FormControl className={classes.formControl}>
          <Autocomplete
            multiple
            id="task_type"
            // disableCloseOnSelect
            options={typeOptions}
            getOptionLabel={(option) => option.label}
            value={typeData}
            disablePortal={true}
            disableClearable={typeData.length <= 1}
            size="small"
            className={classes.taskListFilter}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: '0px !important', minHeight: 'unset' }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={taskFilter?.inclusionFilter?.split(',').includes(option.key)}
                  sx={{ padding: '0rem !important', transform: 'scale(0.6)' }}
                />
                <Typography className={classes.desc} sx={{ padding: '0px !important' }}>
                  {option.label}
                </Typography>
              </li>
            )}
            renderInput={(params) => <TextField {...params} className={classes.desc} variant="standard" label={'Type'} />}
            onChange={(event, newValue) => {
              event.preventDefault()
              event.stopPropagation()
              const selectedKeys = newValue.map((option) => option.key).join(',')
              const updatedFilter = {
                ...taskFilter,
                page: 1,
                inclusionFilter: selectedKeys,
              }

              setTaskFilter(updatedFilter)
              localStorage.setItem(taskPage === 'My' ? 'my_task' : taskPage === 'Top' ? 'top_task' : 'all_task', JSON.stringify(updatedFilter))
            }}
          />
        </FormControl>
      </Stack>
    )
  }

  const renderStatusFilter = () => {
    let typeOptions = [] as any
    if (taskPage === 'Top') {
      typeOptions = [
        { key: 'isCompleted', label: 'Hide completed' },
        { key: 'isHideUpdatedToday', label: 'Hide updated today' },
      ]
    }
    else {
      typeOptions = [
        { key: 'isDueToday', label: 'Due today' },
        { key: 'isAssignedToday', label: 'Assigned today' },
        { key: 'isCompleted', label: 'Hide completed' },
        { key: 'isHideUpdatedToday', label: 'Hide updated today' },
        { key: 'isTopDailyTask', label: 'Top daily task' },
      ]
    }

    const getSelectedValues = () => {
      let filterKeys = [] as any
      if (taskFilter?.isDueToday)
        filterKeys = [...filterKeys, 'isDueToday']
      if (taskFilter?.isAssignedToday)
        filterKeys = [...filterKeys, 'isAssignedToday']
      if (taskFilter?.isCompleted)
        filterKeys = [...filterKeys, 'isCompleted']
      if (taskFilter?.isHideUpdatedToday)
        filterKeys = [...filterKeys, 'isHideUpdatedToday']
      if (taskFilter?.isTopDailyTask)
        filterKeys = [...filterKeys, 'isTopDailyTask']

      return typeOptions.filter((option: { key: string }) => filterKeys.includes(option.key));
    }
    const typeData = getSelectedValues()

    return (
      <Stack direction={'row'}>
        <FormControl className={classes.formControl}>
          {/* <Typography pb={0.5} className={classes.desc} variant="body2"><strong>Status</strong></Typography> */}
          <Autocomplete
            multiple
            id="task_status"
            // disableCloseOnSelect
            options={typeOptions}
            getOptionLabel={(option) => option.label}
            value={typeData}
            disablePortal={true}
            disableClearable={typeData.length <= 1}
            size="small"
            className={classes.taskListFilter}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: '0px !important', minHeight: 'unset' }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={taskFilter[option.key]}
                  sx={{ padding: '0rem !important', transform: 'scale(0.6)' }}
                />
                <Typography {...props} className={classes.desc} sx={{ padding: '0px !important' }}>
                  {option.label}
                </Typography>
              </li>
            )}
            renderInput={(params) => <TextField {...params} className={classes.desc} variant="standard" label={'Status'} />}
            onChange={(e: any, newValue: any) => {
              e.preventDefault()
              e.stopPropagation()
              const updatedFilter = {
                ...taskFilter,
                page: 1,
                isDueToday: false,
                isAssignedToday: false,
                isCompleted: false,
                isHideUpdatedToday: false,
                isTopDailyTask: false,
                ...newValue.reduce((acc: any, option: any) => {
                  acc[option.key] = true
                  return acc
                }, {}),
              }
              setTaskFilter(updatedFilter)
              localStorage.setItem(taskPage === 'My' ? 'my_task' : taskPage === 'Top' ? 'top_task' : 'all_task', JSON.stringify(updatedFilter))
            }}
          />
        </FormControl>
      </Stack>
    )
  };

  const renderPriorityFilter = () => {
    const typeOptions = [
      { key: 'A', label: 'Highest' },
      { key: 'B', label: 'High' },
      { key: 'C', label: 'Medium' },
      { key: 'D', label: 'Low' },
      { key: 'E', label: 'Lowest' },
      { key: 'oth', label: 'Others' }
    ]

    const getSelectedValues = () => {
      if (!taskFilter?.priority) return [];
      const filterKeys = taskFilter.priority.split(',');
      return typeOptions.filter(option => filterKeys.includes(option.key));
    }

    let typeData = getSelectedValues()
    return (
      <Stack direction={'column'}>
        <FormControl className={classes.formControl}>
          <Autocomplete
            multiple
            id="task_type"
            // disableCloseOnSelect
            options={typeOptions}
            getOptionLabel={(option) => option.label}
            value={typeData}
            disablePortal={true}
            disableClearable={typeData.length <= 1}
            size="small"
            className={classes.taskListFilter}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: '0px !important', minHeight: 'unset' }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={taskFilter?.priority?.split(',').includes(option.key)}
                  sx={{ padding: '0rem !important', transform: 'scale(0.6)' }}
                />
                <Typography className={classes.desc} sx={{ padding: '0px !important' }}>
                  {option.label}
                </Typography>
              </li>
            )}
            renderInput={(params) => <TextField {...params} className={classes.desc} variant="standard" label={'Priority'} />}
            onChange={(event, newValue) => {
              event.preventDefault()
              event.stopPropagation()
              const selectedKeys = newValue.map((option) => option.key).join(',')
              const updatedFilter = {
                ...taskFilter,
                page: 1,
                priority: selectedKeys,
              }

              setTaskFilter(updatedFilter)
              localStorage.setItem(taskPage === 'My' ? 'my_task' : taskPage === 'Top' ? 'top_task' : 'all_task', JSON.stringify(updatedFilter))
            }}
          />
        </FormControl>
      </Stack>
    )
  }

  const renderAdHocFilter = () => {
    return (
      <Stack direction={'row'} alignItems="center" spacing={0}>
        <Checkbox
          checked={taskFilter?.includeAdHocToday || false}
          name="show today's ad-hoc tasks"
          color="primary"
          size="small"
          sx={{ padding: '0rem !important', transform: 'scale(0.6)' }}
          onChange={(event, newValue) => {
            console.log(newValue)
            event.preventDefault()
            event.stopPropagation()
            const updatedFilter = {
              ...taskFilter,
              page: 1,
              includeAdHocToday: newValue,
            }

            setTaskFilter(updatedFilter)
            localStorage.setItem(taskPage === 'My' ? 'my_task' : taskPage === 'Top' ? 'top_task' : 'all_task', JSON.stringify(updatedFilter))
          }}
        />

        <Typography className={classes.desc} variant="body2" style={{ maxWidth: 80, whiteSpace: 'normal' }}>
          Show today's ad-hoc tasks
        </Typography>
      </Stack>
    )
  }

  const renderHospitalFilter = () => {
    const fallbackHospitals: Record<string, string> = { lifemd: 'LifeMD', boylston: 'Boylston', healthyheart: 'Healthy Heart', chambercardio: 'ChamberCardio' }
    const meta = (Object.keys(hospitalsMetadata).length > 0) ? hospitalsMetadata : fallbackHospitals
    const hospitalEntries = Object.entries(meta as Record<string, string>)
    if (hospitalEntries.length === 0) return <></>

    const hospitalOptions = hospitalEntries.map(([key, label]) => ({ key, label: label as string }))
    hospitalOptions.sort((a, b) => a.label.localeCompare(b.label))

    const getSelectedValues = () => {
      if (!taskFilter?.hospitalFilter) return []
      const filterKeys = taskFilter.hospitalFilter.split(',')
      return hospitalOptions.filter((option) => filterKeys.includes(option.key))
    }

    const typeData = getSelectedValues()
    return (
      <Stack direction={'column'}>
        <FormControl className={classes.formControl}>
          <Autocomplete
            multiple
            id="hospital_filter"
            options={hospitalOptions}
            getOptionLabel={(option) => option.label}
            value={typeData}
            disablePortal={true}
            disableClearable={typeData.length <= 1}
            size="small"
            className={classes.taskListFilter}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: '0px !important', minHeight: 'unset' }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={taskFilter?.hospitalFilter?.split(',').includes(option.key)}
                  sx={{ padding: '0rem !important', transform: 'scale(0.6)' }}
                />
                <Typography className={classes.desc} sx={{ padding: '0px !important' }}>
                  {option.label}
                </Typography>
              </li>
            )}
            renderInput={(params) => <TextField {...params} className={classes.desc} variant="standard" label={'Hospital'} />}
            onChange={(event, newValue) => {
              event.preventDefault()
              event.stopPropagation()
              const selectedKeys = newValue.map((option) => option.key).join(',')
              const updatedFilter = {
                ...taskFilter,
                page: 1,
                hospitalFilter: selectedKeys,
              }

              setTaskFilter(updatedFilter)
              localStorage.setItem(taskPage === 'My' ? 'my_task' : taskPage === 'Top' ? 'top_task' : 'all_task', JSON.stringify(updatedFilter))
            }}
          />
        </FormControl>
      </Stack>
    )
  }

  const renderTaskData = () => {
    return <>
      <Stack direction={'row'} spacing={4} alignItems="flex-end" justifyContent={'space-between'}
        sx={{ padding: '0.3rem 0rem 1rem 0.8rem' }}>
        <Stack spacing={5} direction={'row'}>
          {renderStatusFilter()}
          {renderTypeFilter()}
          {renderPriorityFilter()}
          {renderHospitalFilter()}
          {taskPage === 'Top' ? renderAdHocFilter() : <></>}
        </Stack>
        <Box alignItems={'center'} mr={0.5}>
          <TextField
            placeholder="search"
            name="search"
            disabled={taskLoading}
            value={searchInput}
            variant="standard"
            size="small"
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ fontSize: '0.75rem', backgroundColor: 'inherit' }}
          />
        </Box>
      </Stack>

      <TableContainer component={Paper} ref={tableEl} sx={getTableStyle()}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {headerCol.map((i: any) => i.isShow &&
                <TableCell align={i.align}
                  className={`${classes.patientListHeadersBorder} ${classes.taskTableHeader}`}
                  sx={{
                    ...i.style,
                    minWidth: i.width || '5rem !important',
                    maxWidth: ['stage', 'program', 'phone'].includes(i.key) ? 'max-content !important' : '',
                    borderTop: 'solid 0.01rem var(--color-border-light) !important',
                  }}>
                  <Typography className={classes.taskTableHeaderTitle}>
                    {i.title}
                    {sortableColumns.includes(i.key) &&
                      <IconButton sx={{ padding: ' 0px !important' }} onClick={() => handleSort(i.key)}>
                        {sortIcon(i.key === taskFilter.sortBy ? taskFilter?.sortOrder : '')}
                      </IconButton>
                    }
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody id="scrollableDivPL">
            {taskData?.map((row: any) => (<>
              <TableRow key={`hd-${row.id}`}
                sx={{
                  boxShadow: (row.id === currTask.id) ? '0px 3px 6px #00000029 !important' : '',
                }}
              >
                {headerCol.map((col: any, index: any) => {
                  return col.isShow &&
                    <TableCell align={col.align}
                      sx={{
                        ...col.style,
                        padding: '0rem 0.15rem 0rem 0.25rem',
                        minWidth: col.width || '4rem !important',
                        cursor: 'pointer',
                        maxWidth: ['stage', 'program', 'phone', 'ref_id', 'MRN'].includes(col.key) ? 'max-content !important' : '',
                        backgroundColor: confirmDelete === row.id
                          ? '#ee18041c'
                          : (row.last_modified_date &&
                            (row.task_status !== 'Completed' && moment.unix(row.last_modified_date).format('MMM DD, YYYY') === moment().format('MMM DD, YYYY'))
                          ) ? '#6bd21914'
                            : (!row?.stage?.display_name || row.task_status === 'Completed')
                              ? '#8a88881a'
                              : ''
                      }}
                      className={col.key === 'title' ? classes.taskNotes : ''}
                      onClick={() => !['name', 'MRN', 'details', 'notes'].includes(col.key) && handleEditTask(row, 'edit')}
                    >
                      {col.key === 'name'
                        ? <Typography className={classes.desc} variant="body2">
                          <Link className={classes.patientsName} underline="none"
                            href={`/patients/${row.patient?.ref_id}/details`}
                          >
                            {`${row.patient?.given_name} ${row?.patient?.family_name}`}
                          </Link>
                        </Typography>
                        : col.key === 'hospital_name'
                          ? <Typography className={classes.desc} variant="body2">
                            {row.patient?.hospital_name || ''}
                          </Typography>
                        : col.key === 'phone'
                          ? <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 'max-content !important' }}>
                              <Typography className={classes.desc} variant="body2">
                                {row?.patient?.phone || ''}
                              </Typography>
                              {row?.patient?.phone && (
                                <Tooltip title="Call with Zoom Phone" placement="bottom">
                                  <IconButton
                                    size="small"
                                    href={getZoomPhoneUri(row.patient.phone)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ padding: '2px' }}
                                  >
                                    <PhoneForwardedIcon sx={{ fontSize: 14, color: 'var(--color-link) !important' }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            {row?.patient?.preferred_contact_time_blocks && (
                              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontSize: '0.65rem', lineHeight: 1.2 }}>
                                {formatContactTime(row.patient.preferred_contact_time_blocks, row.patient.preferred_contact_timezone)}
                              </Typography>
                            )}
                          </Box>
                          : col.key === 'DOB'
                            ? <Typography className={classes.desc} variant="body2">
                              {row?.patient?.birthdate
                                ? moment(row.patient?.birthdate).format('MMM DD YYYY')
                                : ''
                              }
                            </Typography>
                            : col.key === 'ref_id'
                              ? <Typography className={classes.desc} variant="body2">
                                {row.patient?.ref_id || ''}
                              </Typography>
                              : col.key === 'MRN'
                                ? <Typography className={classes.desc} variant="body2">
                                  <Link
                                    className={classes.patientsName}
                                    underline="none"
                                    href={`/patients/${row.patient?.ref_id}/details`}
                                  >
                                    {row.patient?.MRN}
                                  </Link>
                                </Typography>
                                : col.key === 'title'
                                  ? <Tooltip title={(stripPrefixAnywhere(row[col.key]) || row[col.key])?.length > 20 ? stripPrefixAnywhere(row[col.key]) || row[col.key] : ''}>
                                    <Typography
                                      className={`${classes.desc} ${classes.autoFitLabel}`}
                                      variant="body2" width={col.width}
                                    >
                                      {stripPrefixAnywhere(row[col.key]) || row[col.key]}
                                    </Typography>

                                  </Tooltip>
                                  : col.key === 'priority'
                                    ? <Tooltip title={row[col.key]?.length > 20 ? row[col.key] : ''}>
                                      <Typography
                                        className={`${classes.desc} ${classes.autoFitLabel}`}
                                        variant="body2" width={col.width}
                                      >
                                        {renderPriorityIcon(row[col.key])}
                                      </Typography>

                                    </Tooltip>
                                    : col.key === 'subsampled'
                                      ? <Tooltip title={row[col.key]?.length > 20 ? row[col.key] : ''}>
                                        <Typography
                                          className={`${classes.desc} ${classes.autoFitLabel}`}
                                          variant="body2" width={col.width}
                                        >
                                          {renderTopDailyIcon(row[col.key])}
                                        </Typography>

                                      </Tooltip>
                                      : col.key === 'task_status'
                                        ? getTaskStatus(row['task_status'])
                                        : col.key === 'details'
                                          ? <Stack direction={'row'} spacing={0} flexDirection={'inherit'}>
                                            <Tooltip
                                              title={<Card className={classes.taskDetailsTooltip}>
                                                <TaskBpComponent
                                                  input={{
                                                    id: row.id
                                                  }}
                                                />
                                              </Card>
                                              }
                                              componentsProps={{
                                                tooltip: {
                                                  sx: {
                                                    backgroundColor: 'var(--color-background-subtle) !important',
                                                    maxWidth: '35rem !important',
                                                    padding: '0px !important'
                                                  }
                                                }
                                              }}
                                              placement="bottom"
                                            >
                                              <IconButton aria-label='BP details' size='small' sx={{ padding: '5px 0px 0px 0px' }}>
                                                <BloodtypeIcon sx={{ fill: '#e75f5f', fontSize: '0.7rem !important' }} />
                                              </IconButton>
                                            </Tooltip>
                                            {renderDelayIcon(row.delays)}
                                            {row.optional &&
                                              <Tooltip title='Delete' placement="bottom">
                                                <IconButton
                                                  aria-label='Delete'
                                                  size='small'
                                                  sx={{ padding: `5px 0px 0px 0px` }}
                                                  onClick={() => setConfirmDelete(row.id)}
                                                >
                                                  <DeleteOutlinedIcon fontSize="small" sx={{ fontSize: '0.78rem !important' }} />
                                                </IconButton>
                                              </Tooltip>
                                            }
                                            <Tooltip
                                              title={<Card className={classes.taskActivityTooltip}>
                                                <TaskHistoryComponent
                                                  input={{
                                                    patientId: row?.patient?.ref_id,
                                                    isTaskEdit: true,
                                                    task_id: '', //input.currTask.id,
                                                  }}
                                                  updateIsTaskUpdate={() => null}
                                                />
                                              </Card>
                                              }
                                              componentsProps={{
                                                tooltip: {
                                                  sx: {
                                                    backgroundColor: 'var(--color-background-subtle) !important',
                                                    width: '50rem !important',
                                                    padding: '0px !important'
                                                  }
                                                }
                                              }}
                                              placement="bottom"
                                            >
                                              <IconButton
                                                aria-label='Activity'
                                                size='small'
                                                sx={{ padding: '5px 0px 0px 0px' }}
                                              >
                                                <VisibilityOutlinedIcon fontSize="small" sx={{ fill: 'grey', fontSize: '0.7rem !important' }} />
                                              </IconButton>
                                            </Tooltip>
                                            {row.action_type === 'Call' &&
                                              <>
                                                {row?.attempts?.count > 0 ?
                                                  <Tooltip
                                                    title={<Card className={classes.taskDetailsTooltip}>
                                                      <TaskDelayComponent
                                                        input={{
                                                          attempts: row.attempts
                                                        }}
                                                      />
                                                    </Card>
                                                    }
                                                    componentsProps={{
                                                      tooltip: {
                                                        sx: {
                                                          backgroundColor: 'var(--color-background-subtle) !important',
                                                          maxWidth: '35rem !important',
                                                          padding: '0px !important'
                                                        }
                                                      }
                                                    }}
                                                    placement="bottom"
                                                  >
                                                    <Badge badgeContent={row?.attempts?.count} color="primary"
                                                      className={classes.taskAttemptBadge}
                                                      sx={{
                                                        '& .MuiBadge-badge': {
                                                          right: '0px !important',
                                                        }
                                                      }}>
                                                      {renderAttemptIcon()}
                                                    </Badge>
                                                  </Tooltip>
                                                  :
                                                  <Tooltip title='Attempt notes ' placement="bottom">
                                                    {renderAttemptIcon()}
                                                  </Tooltip>
                                                }
                                              </>
                                            }
                                          </Stack>
                                          : (col.key === 'delay_note' &&
                                            ['delayed', 'Intentionally Delayed'].includes(row.task_status)
                                          ) ?
                                            <Tooltip title={(row?.delays?.latest_delay?.justification || '')?.length > 20 ? row?.delays?.latest_delay?.justification : ''}>
                                              <Typography
                                                className={`${classes.desc} ${classes.autoFitLabel}`}
                                                variant="body2" width={col.width}
                                              >
                                                {row?.delays?.latest_delay?.justification || ''}
                                              </Typography>
                                            </Tooltip>
                                            : col.key === 'notes'
                                              ? <Box sx={{ cursor: 'pointer' }}>
                                                {currTaskNote.id === row.id
                                                  ? <Tooltip title={currTaskNote.note}>
                                                    <TextField
                                                      variant="standard"
                                                      value={currTaskNote.note}
                                                      autoFocus
                                                      onChange={(e: any) => updateNote(e)}
                                                      onKeyUp={(e: any) => {
                                                        e.preventDefault();
                                                        if (e.key === 'Enter' && currTaskNote.note !== '')
                                                          handleSaveNote()
                                                      }}
                                                      className={classes.desc}
                                                      InputProps={{
                                                        endAdornment: (
                                                          <InputAdornment position="end">
                                                            <IconButton edge="end" color="primary"
                                                              onClick={() => handleSaveNote()}>
                                                              <SaveIcon
                                                                className={classes.patientTaskIcon}
                                                                sx={{ fill: 'var(--color-primary)', paddingRight: '0.2rem' }}
                                                              />
                                                            </IconButton>
                                                            <IconButton size="small"
                                                              onClick={() => setCurrTaskNote({})}>
                                                              <CloseIcon className={classes.cardValue} />
                                                            </IconButton>
                                                          </InputAdornment>
                                                        ),
                                                      }}
                                                    />
                                                  </Tooltip>
                                                  : <Typography className={classes.desc}
                                                    variant="body2"
                                                    onClick={() => handleNoteOpen(
                                                      row.id,
                                                      Object.keys(row.notes || {})?.length > 0 ? row.notes[0].text : ''
                                                    )}
                                                  >
                                                    {Object.keys(row.notes || {})?.length > 0
                                                      ? getGeneralNote(currTaskNote.id === row.id ? currTaskNote.note : row.notes[0].text)
                                                      : <Typography component={'span'} sx={{ visibility: 'hidden' }}>Note</Typography>
                                                    }
                                                  </Typography>
                                                }
                                              </Box>
                                              : <Typography className={classes.desc} variant="body2"
                                                sx={{ width: ['stage', 'program'].includes(col.key) ? 'max-content!important' : 'inherit' }}
                                              >
                                                {col.key === 'stage'
                                                  ? <>
                                                    {row?.stage?.display_name || 'General'}
                                                    {row.optional === false
                                                      ? <IconButton
                                                        aria-label='Required'
                                                        size='small'
                                                        sx={{ padding: '0px 0px 0.1rem 0.1rem' }}
                                                      >
                                                        <StarRateIcon className={classes.patientTaskIcon} sx={{ fontSize: '0.45rem !important', fill: 'red' }} />
                                                      </IconButton>
                                                      : ''
                                                    }
                                                  </>
                                                  : col.key === 'owner'
                                                    ? row?.owner?.name
                                                    : col.key === 'assigned_to'
                                                      ? row?.assignee?.name
                                                      : ['due_date', 'assignment_date', 'completion_date'].includes(col.key)
                                                        ? <Typography className={classes.taskHistoryTitle} variant="body2">
                                                          {row[col.key] && moment(row[col.key]).format('MMM DD, YYYY')}
                                                        </Typography>
                                                        : col.key === 'program'
                                                          ? getPrgName(row.program_name)
                                                          : row[col.key]
                                                }
                                              </Typography>
                      }
                    </TableCell>
                })}
              </TableRow>
              {row.id === currTask.id && currTask.action !== 'BP' &&
                <TableRow> {taskLoading && <LinearProgress />}
                  <TableCell colSpan={headerCol.length}>
                    {currTask.action === 'edit'
                      ?
                      <TaskActionComponent
                        input={{
                          currTask: currTask,
                          isPatientTask: false,
                          isTaskPanel: false,
                          users: usersList,
                        }}
                        updateTaskData={updateTaskData}
                        closeTaskAction={handleCloseTaskAction}
                      />
                      : <TaskActionComponent
                        input={{
                          currTask: currTask,
                          isPatientTask: false,
                        }}
                        updateTaskData={updateTaskData}
                        closeTaskAction={handleCloseTaskAction}
                      />
                    }
                  </TableCell>
                </TableRow>
              }
            </>
            ))}
            {!taskLoading && patientDetailStatus !== 'loading' && taskData.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} align='center'>
                  <Typography className={classes.desc} variant="body2">Data not available!</Typography>
                </TableCell>
              </TableRow>
            )}
            {taskLoading && (
              <TableRow>
                <TableCell colSpan={14} align='center'>
                  <Typography className={classes.desc} variant="body2">Loading...</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  }

  return (
    <Box sx={{ minHeight: '21.5rem' }}>
      <Box pt={1} pl={1.6}>
        <Typography className={classes.desc} variant="body2">
          {!taskLoading ?
            taskTotal.showing > 0 ? `Showing ${taskTotal.showing} out of  ${taskTotal.total} tasks` : ''
            : <CircularProgress size={10} />
          }
        </Typography>
      </Box>

      {isInitialLoad && renderTaskData()}

      {Boolean(confirmDelete) &&
        <ConfirmationDialogComponent
          open={Boolean(confirmDelete)}
          content={`Are you sure you want to delete task?`}
          handleConfirmClose={handleConfirmDelete}
          confirmStatus={confirmDeleteTask}
        />
      }
    </Box>
  );
}

export default withStyles(stylesheet)(AllTaskListComponent);